import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { PWAInstallLoginPrompt } from '../components/PWAInstall';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // Branded Google OAuth availability — flips when GOOGLE_OAUTH_CLIENT_ID +
  // GOOGLE_OAUTH_CLIENT_SECRET are set in backend/.env. Falls back to the
  // Emergent broker while unconfigured so login never breaks.
  const [brandedGoogleReady, setBrandedGoogleReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/auth/google-oauth/status`).then(r => {
      if (mounted) setBrandedGoogleReady(!!r.data?.configured);
    }).catch(() => { /* keep false */ });
    return () => { mounted = false; };
  }, []);

  // Surface a Google sign-in rejection bounced back as ?oauth_error=... — most
  // importantly when a Google account isn't registered as a venue/producer.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('oauth_error');
    if (!code) return;
    const messages = {
      not_registered: "This Google account isn't registered. Ask your admin to add your email as a venue or producer first.",
      account_disabled: 'Your account is disabled. Please contact your admin.',
      access_denied: 'Google sign-in was cancelled.',
    };
    toast.error(messages[code] || 'Google sign-in failed. Please try again.');
    // Clean the URL so the error doesn't persist on refresh.
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (brandedGoogleReady) {
      window.location.href = `${API}/auth/google-oauth/login?redirect=/`;
      return;
    }
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] bg-[#FF007F] rounded-full opacity-[0.07] blur-[150px]" />
        <div className="absolute bottom-[-200px] right-[-200px] w-[500px] h-[500px] bg-[#39FF14] rounded-full opacity-[0.05] blur-[150px]" />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4" data-testid="logo">
            <div className="absolute -bottom-4 -left-6 w-32 h-32 rounded-full bg-[#FF007F] opacity-30 blur-3xl pointer-events-none" />
            <div className="absolute -top-4 -right-6 w-32 h-32 rounded-full bg-[#39FF14] opacity-25 blur-3xl pointer-events-none" />
            <img src="/brand/mb-icon.png" alt="MusicBooth" className="relative w-32 h-32 object-contain" draggable={false} />
          </div>
          <h1 className="text-white text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>MusicBooth</h1>
          <p className="text-[#A0A0A0] text-xs mt-1 tracking-[0.3em] uppercase font-mono">
            <span className="bg-gradient-to-r from-[#39FF14] to-[#00BFFF] bg-clip-text text-transparent font-semibold">Your venue. Our vibe.</span>
          </p>
        </div>

        <div className="glass-surface rounded-2xl p-7" data-testid="login-card">
          <h2 className="text-xl font-bold text-white mb-1 tracking-wide" style={{ fontFamily: 'Outfit' }}>Welcome back</h2>
          <p className="text-[#A0A0A0] text-sm mb-6">Sign in to your venue</p>

          <Button
            onClick={handleGoogleSignIn}
            data-testid="google-signin-button"
            className="w-full h-12 bg-white hover:bg-gray-100 text-black font-semibold rounded-full tracking-wide transition-all hover:scale-[1.02] flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
              <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
            </svg>
            Sign in with Google
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5" aria-hidden>
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#606060] font-semibold">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-4" data-testid="email-login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A0A0A0] text-xs uppercase tracking-widest font-semibold">Email</Label>
              <Input id="email" type="email" placeholder="you@venue.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="bg-[#000000]/60 border-white/8 text-white h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A0A0A0] text-xs uppercase tracking-widest font-semibold">Password</Label>
              <Input id="password" type="password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="bg-[#000000]/60 border-white/8 text-white h-11 rounded-xl" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 bg-[#FF007F] hover:bg-[#CC0066] text-white font-bold rounded-full tracking-wider">
              {loading ? 'Signing in...' : 'SIGN IN WITH EMAIL'}
            </Button>
          </form>
        </div>

        <PWAInstallLoginPrompt />

        <div className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-[#606060] font-mono flex justify-center gap-5">
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="login-privacy-link">Privacy</Link>
          <span className="opacity-30">·</span>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="login-terms-link">Terms</Link>
          <Link to="/refund" className="hover:text-white transition-colors" data-testid="login-refund-link">Refund</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
